import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import Column from "@amzn/meridian/column";
import Table, { TableRow, TableCell } from "@amzn/meridian/table"
import { TableSortDirection } from "@amzn/meridian/table/table";
import Input from "@amzn/meridian/input";
import Pagination from "@amzn/meridian/pagination"
import Row from "@amzn/meridian/row";
import Select, { SelectOption } from "@amzn/meridian/select"
import Text from "@amzn/meridian/text";
import GreenCheckMarkWithTooltip from "./GreenCheckMarkWithTooltip";
import RedCrossWithTooltip from "./RedCrossWithTooltip";
import BlueScannedPreviouslyWithTooltip from "./BlueScannedPreviouslyWithTooltip";
import AssociateAliasCell from "./AssociateAliasCell";
import TrainedRolesCell from "./TrainedRolesCell";
import {
  AUDIT_TABLE_OBJECT_LIST,
  OVERALL_AUDIT_TABLE_OBJECT_LIST,
  AUDIT_NO_SCANS_MESSAGE,
  ASSOCIATE_ALIAS_FIELDNAME,
  DEFAULT_ITEMS_PER_PAGE,
  SEGMENT_FIELDNAME,
  TRAINED_ROLES_FIELDNAME,
  BADGE_IN_STATUS_FIELDNAME,
  LABOR_TRACKED_FIELDNAME,
  TIMESTAMP_FIELDNAME,
  ASSIGNED_ROLE_FIELDNAME,
  DROPDOWN_TYPE,
} from "../../constants";
import { formatSegment } from "./segmentTime";
import "./AuditComponents.scss";
import orderBy from "lodash/orderBy";
import SearchableSelect from "./SearchableSelect";
import uniq from "lodash/uniq";
import debounce from "lodash/debounce";

const BADGE_STATUS_TOKEN_MAP = {
  "Scanned": <><GreenCheckMarkWithTooltip />{" "}Scanned</>,
  "Not Scanned": <><RedCrossWithTooltip />{" "}Not Scanned</>,
  // Overall (CH) view only — the backend derives this status; per-segment views never emit it.
  "Scanned Previously": <><BlueScannedPreviouslyWithTooltip />{" "}Scanned Previously</>,
}

export default function AuditDetailsTable({
  auditRecordList,
  showSegmentColumn = false,
  segmentTimeById = {},
  segmentOffsetMinutes = null as number | null,
  defaultItemsPerPage = DEFAULT_ITEMS_PER_PAGE,
}) {
  const { nodeId } = useParams<{ nodeId: string }>();
  const [userFilters, setUserFilters] = useState({});
  const [debouncedFilters, setDebouncedFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const [sortColumn, setSortColumn] = useState(TIMESTAMP_FIELDNAME);
  const [sortDirection, setSortDirection] = useState<TableSortDirection>("ascending");

  // The Overall audit tab prepends a "Segment" column; the per-segment view is unchanged.
  const columnList = showSegmentColumn ? OVERALL_AUDIT_TABLE_OBJECT_LIST : AUDIT_TABLE_OBJECT_LIST;

  // Reset to page 1 whenever the data source changes. A refresh (5-min timer / manual) can
  // return a smaller record set; without this, a stale currentPage could point past the end
  // and render an empty page until the user clicks. Matches the "fresh data → fresh page"
  // intent the per-segment view gets from its key={selectedSegmentId} remount.
  useEffect(() => {
    setCurrentPage(1);
  }, [auditRecordList]);

  // Debounce filter updates to reduce computation
  const debouncedSetFilters = useMemo(
      () => debounce((filters) => setDebouncedFilters(filters), 300),
      []
  );

  useEffect(() => {
    debouncedSetFilters(userFilters);
  }, [userFilters, debouncedSetFilters]);

  // Memoize dropdown options to prevent recalculation
  const dropdownOptionsCache = useMemo(() => {
    const cache = {};
    columnList.forEach(({ fieldName, fieldType }) => {
      if (fieldType === DROPDOWN_TYPE) {
        if (fieldName === TRAINED_ROLES_FIELDNAME) {
          const fieldValueOptions = auditRecordList
              .filter(record => record[fieldName] !== null)
              .flatMap(record => record[fieldName]);
          cache[fieldName] = uniq(fieldValueOptions);
        } else if (fieldName === LABOR_TRACKED_FIELDNAME) {
          cache[fieldName] = ["Yes", "No"];
        } else {
          const fieldValueOptions = auditRecordList.map(record => record[fieldName]);
          cache[fieldName] = uniq(fieldValueOptions);
        }
      }
    });
    return cache;
  }, [auditRecordList, columnList]);

  // Optimized filter method with early returns
  const filterMethod = useCallback((userAuditRecord) => {
    for (const key in debouncedFilters) {
      const filterValue = debouncedFilters[key];
      if (!filterValue) continue;

      const recordValue = userAuditRecord[key];
      if (recordValue === undefined || recordValue === null) {
        // For laborTracked, null/undefined should be treated as "No"
        if (key === LABOR_TRACKED_FIELDNAME) {
          const expectedValue = filterValue.toLowerCase() === "yes";
          if (expectedValue) return false; // Looking for "Yes" but record is null/undefined
        } else {
          return false;
        }
        continue;
      }

      if (Array.isArray(recordValue)) {
        // trainedRoles - use some() for early termination
        if (!recordValue.some(role =>
            role.toString().toLowerCase().includes(filterValue.toLowerCase())
        )) return false;
      } else if (key === LABOR_TRACKED_FIELDNAME) {
        // Handle laborTracked specifically - could be boolean, string, or other types
        const expectedValue = filterValue.toLowerCase() === "yes";
        let actualValue = false;

        if (typeof recordValue === 'boolean') {
          actualValue = recordValue;
        } else if (typeof recordValue === 'string') {
          actualValue = recordValue.toLowerCase() === 'true' || recordValue.toLowerCase() === 'yes';
        } else {
          actualValue = Boolean(recordValue);
        }

        if (actualValue !== expectedValue) return false;
      } else if (key === BADGE_IN_STATUS_FIELDNAME) {
        // Exact match, not startsWith — the three statuses share a prefix ("Scanned" vs
        // "Scanned Previously"), and the filter is a dropdown of exact known values, so a
        // "Scanned" selection must not also match "Scanned Previously" rows.
        if (recordValue.toLowerCase() !== filterValue.toLowerCase()) return false;
      } else {
        // Handle string fields (AssociateAlias, AssignedRole, etc.) with partial matching
        const stringValue = recordValue?.toString() || "";
        if (!stringValue.toLowerCase().includes(filterValue.toLowerCase())) return false;
      }
    }
    return true;
  }, [debouncedFilters]);

  // Memoize filtered and sorted data
  const processedData = useMemo(() => {
    let filtered = auditRecordList.filter(filterMethod);

    if (sortColumn) {
      filtered = orderBy(filtered, [sortColumn], [sortDirection === "ascending" ? "asc" : "desc"]);
    }

    return filtered;
  }, [auditRecordList, filterMethod, sortColumn, sortDirection]);

  // Pagination calculations
  const firstVisibleIndex = (currentPage - 1) * itemsPerPage;
  const lastVisibleIndex = Math.min(firstVisibleIndex + itemsPerPage, processedData.length);
  const numberOfPages = Math.ceil(processedData.length / itemsPerPage);
  const visibleData = processedData.slice(firstVisibleIndex, lastVisibleIndex);

  const formatTimestampCell = useCallback((timestampStr) => {
    if (!timestampStr || !timestampStr.includes("T") || !timestampStr.includes("Z")) {
      return "";
    }
    return timestampStr.split("T")[1].split("Z")[0].split(".")[0];
  }, []);

  const getTableCellValueFromFieldName = useCallback((auditRecord, fieldName) => {
    switch (fieldName) {
      case SEGMENT_FIELDNAME: {
        // Overall tab only: render the segment's time range from its epoch times,
        // falling back to the raw id when the segment has no time metadata.
        const segId = auditRecord[fieldName] || "";
        return formatSegment({ segmentId: segId, ...segmentTimeById[segId] }, segmentOffsetMinutes);
      }
      case TIMESTAMP_FIELDNAME:
        return formatTimestampCell(auditRecord[fieldName]);
      case ASSOCIATE_ALIAS_FIELDNAME:
        const ppaUrl = `https://fclm-portal.amazon.com/employee/ppaTimeDetails?employeeId=${auditRecord[fieldName]}&warehouseId=${nodeId}`;
        return <AssociateAliasCell ppaUrl={ppaUrl} associateAlias={auditRecord[fieldName]} />;
      case TRAINED_ROLES_FIELDNAME:
        const trainedRolesStr = auditRecord[fieldName]?.join(",") || "";
        return <TrainedRolesCell trainedRolesStr={trainedRolesStr} />;
      case BADGE_IN_STATUS_FIELDNAME:
        return BADGE_STATUS_TOKEN_MAP[auditRecord[fieldName]];
      case LABOR_TRACKED_FIELDNAME:
        return auditRecord[fieldName] ? "Yes" : "No";
      default:
        return auditRecord[fieldName];
    }
  }, [nodeId, formatTimestampCell, segmentTimeById, segmentOffsetMinutes]);

  const changeUserFiltersHandler = useCallback((fieldName) => (inputValue) => {
    setUserFilters(prev => ({
      ...prev,
      [fieldName]: inputValue
    }));
    setCurrentPage(1);
  }, []);

  const sortAuditRecordsHandler = useCallback((event) => {
    setSortColumn(event.sortColumn);
    setSortDirection(event.sortDirection);
  }, []);

  const getInputFilter = useCallback((title, fieldName, fieldType) => {
    if (!auditRecordList.length) return null;

    if (fieldType === DROPDOWN_TYPE) {
      return (
          <SearchableSelect
              userSelectValue={userFilters[fieldName]}
              changeValueHandler={changeUserFiltersHandler}
              fieldName={fieldName}
              title={title}
              options={dropdownOptionsCache[fieldName] || []}
          />
      );
    }

    return (
        <Input
            key={`${fieldName}-filter`}
            placeholder={title}
            value={userFilters[fieldName] || ""}
            onChange={changeUserFiltersHandler(fieldName)}
        />
    );
  }, [auditRecordList.length, userFilters, changeUserFiltersHandler, dropdownOptionsCache]);

  // Gated on the raw list, not the filtered result, so filtering to zero still shows the table.
  if (!auditRecordList.length) {
    return <Text>{AUDIT_NO_SCANS_MESSAGE}</Text>;
  }

  return (
      <Column>
        <Table
            headerRows={1}
            stickyHeaderRow
            showDividers
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={sortAuditRecordsHandler}
            layout="fixed"
        >
          <TableRow>
            {columnList.map(({ title, fieldName }) => (
                <TableCell key={title} sortColumn={fieldName}>{title}</TableCell>
            ))}
          </TableRow>
          <TableRow>
            {columnList.map(({ title, fieldName, fieldType }) => (
                <TableCell key={`${title}-input`}>
                  {getInputFilter(title, fieldName, fieldType)}
                </TableCell>
            ))}
          </TableRow>
          {visibleData.map((auditRecord, index) => {
            const { assignedRole, associateLaborTrackType, associateAlias, timestamp, segmentId } = auditRecord;
            return (
                <TableRow key={`${segmentId || ""}-${associateAlias}-${associateLaborTrackType}-${assignedRole}-${timestamp || index}-row`}>
                  {columnList.map(({ fieldName }) => (
                      <TableCell key={`${segmentId || ""}-${associateAlias}-${associateLaborTrackType}-${timestamp || index}-${fieldName}-cell`}>
                        {getTableCellValueFromFieldName(auditRecord, fieldName)}
                      </TableCell>
                  ))}
                </TableRow>
            );
          })}
        </Table>
        <Row alignmentHorizontal="justify">
          <Row>
            <Text>{`Showing ${firstVisibleIndex + 1} to ${lastVisibleIndex} of ${processedData.length} entries`}</Text>
            <Select value={itemsPerPage} onChange={setItemsPerPage} size="small">
              <SelectOption value={10} label="10" />
              <SelectOption value={20} label="20" />
              <SelectOption value={30} label="30" />
              <SelectOption value={40} label="40" />
              <SelectOption value={50} label="50" />
            </Select>
            <Text>per page</Text>
          </Row>
          <Pagination
              showSkipArrows={true}
              numberOfPages={numberOfPages}
              onChange={setCurrentPage}
              currentPage={currentPage}
          />
        </Row>
      </Column>
  );
}